import { useI18nContext } from '@/core/providers/I18nProvider';
import {
  getAmenityCatalog,
  getAmenityCatalogRevision,
  preloadAmenityCatalog,
  subscribeAmenityCatalog,
  type AmenityCatalogEntry,
} from '@/core/services/amenityCatalogService';
import {
  normalizeAmenities,
  normalizeAmenityName,
} from '@/modules/property-details/services/amenityService';
import { useEffect, useMemo, useState } from 'react';

export interface WebAmenity {
  readonly value: string;
  readonly label: string;
  readonly iconUrl: string | null;
}

interface RankedAmenity extends WebAmenity {
  readonly matched: boolean;
  readonly order: number;
  readonly sourceIndex: number;
}

function catalogByNormalizedValue(
  catalog: ReadonlyMap<string, AmenityCatalogEntry>,
): Map<string, AmenityCatalogEntry> {
  const normalized = new Map<string, AmenityCatalogEntry>();
  for (const entry of catalog.values()) {
    const key = normalizeAmenityName(entry.value);
    if (key && !normalized.has(key)) {
      normalized.set(key, entry);
    }
  }
  return normalized;
}

/**
 * Maps property API amenity values to the localized Contentful catalog loaded
 * centrally with the translations.
 *
 * Match order: exact `value` → case/whitespace/_/- insensitive → unmatched.
 * Unmatched values keep the original label, use the default icon, and sort
 * after all Contentful-ranked matches.
 */
export function useWebAmenities(
  amenities: readonly string[] | null | undefined,
): WebAmenity[] {
  const { currentLanguage } = useI18nContext();
  const [catalogRevision, setCatalogRevision] = useState(
    getAmenityCatalogRevision,
  );

  useEffect(() => {
    return subscribeAmenityCatalog(() => {
      setCatalogRevision(getAmenityCatalogRevision());
    });
  }, []);

  const hasAmenities = (amenities?.length ?? 0) > 0;
  useEffect(() => {
    if (hasAmenities) {
      void preloadAmenityCatalog(currentLanguage);
    }
  }, [hasAmenities, currentLanguage]);

  return useMemo(() => {
    const catalog = getAmenityCatalog(currentLanguage);
    const normalizedCatalog = catalogByNormalizedValue(catalog);

    const ranked: RankedAmenity[] = normalizeAmenities(amenities).map(
      (value, sourceIndex) => {
        const match =
          catalog.get(value) ??
          normalizedCatalog.get(normalizeAmenityName(value));

        if (!match) {
          return {
            value,
            label: value,
            iconUrl: null,
            matched: false,
            order: Number.MAX_SAFE_INTEGER,
            sourceIndex,
          };
        }

        return {
          value,
          label: match.label,
          iconUrl: match.iconUrl,
          matched: true,
          order: match.order,
          sourceIndex,
        };
      },
    );

    ranked.sort((left, right) => {
      if (left.matched !== right.matched) {
        return left.matched ? -1 : 1;
      }
      if (left.matched && left.order !== right.order) {
        return left.order - right.order;
      }
      return left.sourceIndex - right.sourceIndex;
    });

    return ranked.map(({ value, label, iconUrl }) => ({
      value,
      label,
      iconUrl,
    }));
    // catalogRevision re-resolves labels once the central preload lands.
  }, [amenities, currentLanguage, catalogRevision]);
}
